import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { Typography, Link, Button, Box, Stack, Container, useMediaQuery, Modal, Alert, Card, CardContent } from "@mui/material";
import { DataGrid, getDataGridUtilityClass, gridClasses, renderActionsCell } from '@mui/x-data-grid'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import IconButton from '@mui/material/IconButton';

// local
import { passingScores, colorTheme } from '../constants';
import getThisUser from './helpers/getThisUser';
import getUserStatsTable from './helpers/getUserStatsTable';
import formatTimeRemaining from './utilities/formatTimeRemaining';
import CardTable from './widgets/CardTable/index.tsx';

const ChooseExam = () => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery("(max-width:600px)");
    const theme = colorTheme.light;
    const [error, setError] = useState("");
    
    const [user, setUser] = useState("");
    const [examRows, setExamRows] = useState([]);

    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleCardClick = (row) => {
        if (row.timeRemaining === 0) {
            setError('This Exam has expired');
            handleClose();
            return;
        }
        localStorage.setItem('exam_id', row.examId);
        navigate(`/cvesd-iaComponents/${row.examId}`);
    }


    const triggerNav = (examId) => navigate(`/cvesd-iaComponents/${examId}`)

    const gridColumns = [
        { headerName: 'ID', field: 'id'},
        { flex: 0.5, headerName: 'Exam ID', field: 'examId'},
        { flex: 1, headerName: 'Date Taken (YYYY-MM-DD)', field:'dateTaken'},
        { flex: 0.5, headerName: 'Math', field: 'mathScore', renderCell: (params) => <span>{params.value === null ? '--' : params.value}</span>},
        { flex: 0.5, headerName: 'Language', field: 'languageScore', renderCell: (params) => <span>{params.value === null ? '--' : params.value}</span>},
        { flex: 0.5, headerName: 'Reading', field: 'readingScore', renderCell: (params) => <span>{params.value === null ? '--' : params.value}</span>},
        { flex: 0.5, headerName: 'Time Left', field:'timeRemaining', renderCell: (params) => <span>{formatTimeRemaining(params.row.timeRemaining)}</span>},
        {
            field: 'view',
            headerName: '',
            width:60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        if (params.row.timeRemaining === 0) {
                            setError('This Exam has expired');
                            handleClose();
                            return;
                        } else {
                            const examId = params.row.examId;
                            console.log('Exam already exists, opening existing exam...')
                            localStorage.setItem('exam_id',examId);
                            navigate(`/cvesd-iaComponents/${examId}`)
                        }
                        
                    }}
                    aria-label="view answers"
                >
                    <ArrowOutwardIcon sx={{color:'#7D79D7'}}/>
                </IconButton>
            )
        }
    ];
    const paginationModel = { page: 0, pageSize: 5 };

    useEffect(() => {
        getThisUser(setUser);
        getUserStatsTable(setExamRows);
    }, []);
    
    const queryExam = async (user) => {
        if (!user || !user.schoolDistrict) {
            console.error("User data is not loaded or missing school_district.");
            return;
        } 
        if(examRows.length !== 0 ) {
            setOpen(true);
        } else {
            createExam(user);
        }

    }

    const createExam = async (user) => {
        if (!user || !user.schoolDistrict) {
            setError("User data is not loaded or missing school_district.");
            return;
        } 

        if (localStorage.getItem("exam_tokens") === '0') {
            setError('Insufficient Exam Tokens, please contact your district.');
            handleClose();
            return;
        }

        const token = localStorage.getItem("access_token");
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/exams/`, {
                test_taker : user.userId,
                school_district : user.schoolDistrict,
                time_remaining: 7200
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            
            try {
                const examId = response.data.id;
                if(token) {
                    axios
                    .post(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${examId}/create_exam_component/`,{},{
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    })
                    .then((response) => {
                        console.log("Exam created");
                        localStorage.setItem("exam_id", examId);
                        triggerNav(examId);
                      });
                }
            } catch (error) {
                console.error("Error creating exam components:", error.response?.data || error.message);
            }
        } catch (error) {
            console.error("Error creating exam:", error.response?.data || error.message);
        }
    }

    const bigStyling = {display:'flex', flexDirection:'column', maxWidth:'80%',justifySelf:'center'};
    const mobileStyling = {display:'flex', flexDirection:'column', maxWidth:'100%',justifySelf:'center'}

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '65vw',
        minWidth:'65vw',
        bgcolor: 'background.paper',
        borderRadius: 4,
        boxShadow: 24,
        display:'flex',
        p:1,
        flexDirection:'column',
        [`.${gridClasses.cell}.fail`]: {
          backgroundColor: '#FF9494',
          color: '#000000',
        },
        [`.${gridClasses.cell}.pass`]: {
          backgroundColor: '#A3FFAE',
          color: '#000000',
        },
        [`.${gridClasses.cell}.null`]: {
          backgroundColor: '#EBEBEB',
          color: '#000000',
        },
      }
    const mobileModalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90vw',
        height: '90vh',
        bgcolor: 'background.paper',
        borderRadius: 4,
        boxShadow: 24,
        p: 1,
      }

    const headerStyle = {
        bgcolor: '#A6A4CC',
        color:'#FFFFFF',
        px:2,
        pt:2,
        borderTopRightRadius:16,
        borderTopLeftRadius:16,
        pb:1,
    }

    const headerTextStyle = {fontWeight:650, textShadow: '2px 2px 4px rgba(0, 0, 0, 0.25)'}

    const gridStyle = {m:1, borderRadius:4, cursor:'pointer'}

    const cellClassStyle = (params) => {
        if (!['readingScore', 'languageScore', 'mathScore'].includes(params.field)) return '';
        if (params.value === null || params.value === undefined) return 'null';
        return params.value >= passingScores.langauge && ['readingScore', 'languageScore'].includes(params.field) 
            ? 'pass' 
            : params.value >= passingScores.math && params.field==='mathScore' 
            ? 'pass' 
            : 'fail';
    }
    
    return(
        <Box sx={!isMobile ? (bigStyling) : (mobileStyling)}>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
            <Box sx={!isMobile ? (modalStyle) : (mobileModalStyle)}>
                <Box sx={headerStyle}>
                    <Typography variant="h4" sx={headerTextStyle}>
                        Your Exams
                    </Typography>
                </Box>
                {!isMobile ? (
                    <DataGrid 
                        sx={gridStyle}
                        rows={examRows}
                        columns={gridColumns}
                        initialState={{ 
                        pagination: { paginationModel },
                        sorting: {
                            sortModel: [{ field: 'examId', sort: 'desc' }]
                        },
                        columns: {
                            columnVisibilityModel: { id: false }
                        }
                        }}
                        pageSizeOptions={[5, 10]}
                        getCellClassName={cellClassStyle}
                    />
                ) : (
                    <CardTable 
                        rows={examRows}
                        sort={{key:'examId', direction:'desc'}}
                        columns={gridColumns}
                        onClick={handleCardClick}
                        columnVisibilityModel={{id:false, view:false}}
                        getCellClassName={cellClassStyle}
                        sx={{height:'80%', overflowY: 'scroll'}}
                    />
                )
                }
                <Stack direction={'row'} sx={
                    isMobile ? {pb:1, justifyContent:'space-between', position:'fixed', bottom:0, justifySelf:'center', width:'95%'}
                    : {p:0, justifyContent:'space-between'}
                }>
                    <Button variant={'outlined'} sx={{width:'100%', borderColor:'#A6A4CC', color:'#7D79D7'}} onClick={() => {createExam(user)}}>New Exam +</Button>
                </Stack>
            </Box>
            </Modal>

            <Box sx={{ width:'100%', mb:1 }}>
                {error && <Alert severity='error' sx={{mb:1}} onClose={() => {setError('')}}>{error}</Alert>}
            </Box>  
            <Typography variant='h4' sx={{mb:1,color:'#054fb9', fontWeight:700}}>Exams</Typography>
            <Box sx={{display:'flex', flexDirection:'row', bgcolor:'#EBEBEB', borderRadius:5}}>
                {!isMobile && (
                    <Box
                    component="img"
                    src="/images/IA-SA-ProficiencyExamLogo.png"
                    alt="two silhouettes"
                    sx={{ width: 100, height: "auto", maxWidth: "100%",mr:2, bgcolor:'#8babf1', borderTopLeftRadius:20,borderBottomLeftRadius:20, p:2}}
                    />
                )}
                
                <Box sx={{p:2}}>
                    <Link variant='h5' underline='hover' sx={{fontWeight:500, cursor:'pointer'}} onClick={() => queryExam(user)}>
                        Instructional Assistant/Student Attendant Proficiency Exam
                    </Link>
                    <Typography variant='body1' sx={{mt:1}}>
                        This exam is designed to assess your proficiency in the areas of math, language, and reading
                        that will be present in a position as an Instructional Aide or Student Attendant.
                    </Typography>
                </Box>
            </Box>          
        </Box>
    );
}
export default ChooseExam;